import classNames from 'classnames'
import { useEffect, useState } from 'react'

import { Button } from 'core-components/library/button'
import { BaseCrud } from 'core-components/types'

import { PageControl } from './paging/PageControl'
import { SelectRowsCount } from './paging/SelectRowsCount'
import { IRowsPerPageOption, TTitleRenderFunction } from './types'

import styles from './Paging.module.scss'

interface IPagingProps<T, R> {
  source: BaseCrud<T, R>
  total: number
  pageSize?: number
  wip: boolean
  rowsPerPageOptions?: IRowsPerPageOption[]
  titleRender?: TTitleRenderFunction
  pageSizeSelectorVisible?: boolean
}

export const Paging = <T, R>({
  source,
  total,
  wip,
  pageSize,
  rowsPerPageOptions,
  titleRender,
  pageSizeSelectorVisible = true,
}: IPagingProps<T, R>) => {
  const paging = source.getPaging()
  const currentPage = paging.startPage === 0 ? paging.page + 1 : paging.page
  const currentRowsPerPage = pageSize || source.getPaging().pageSize

  const hasFullTotal = total - 1 > currentRowsPerPage // 1 more for next page in default behaviour
  const showNext = hasFullTotal
    ? total > source.getPaging().pageSize * currentPage
    : total >= source.getPaging().pageSize

  const [pages, setPages] = useState<number[]>([])

  useEffect(() => {
    setPages([...Array(currentPage + 1).keys()].filter((el) => el > 0))
  }, [currentPage])

  useEffect(() => {
    const sourcePaging = source.getPaging()
    if (pageSize && sourcePaging.pageSize !== pageSize) {
      source.setPaging({
        startPage: sourcePaging.startPage,
        pageSize: pageSize,
        page: sourcePaging.page,
      })
    }
  }, [pageSize])

  const changePage = (newPage: number) => {
    source.setPaging({
      startPage: paging.startPage,
      pageSize: currentRowsPerPage,
      page: newPage,
    })
  }

  return (
    <div className={styles.root}>
      {pageSizeSelectorVisible ? (
        <SelectRowsCount<T, R>
          className={styles.dropdown}
          source={source}
          pageSize={currentRowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          titleRender={titleRender}
        />
      ) : null}
      <div className={styles.buttons}>
        <Button
          className={classNames(styles.navBtn, styles['navBtn--prev'])}
          iconButton={'kbase kbase-omni-chevron-left'}
          variant={'text'}
          color={'grey'}
          disabled={currentPage === 1 || wip}
          onClick={() => {
            const delta = paging.startPage === 0 ? 2 : 1
            changePage(currentPage - delta)
          }}
        />
        <PageControl
          changePage={changePage}
          pages={pages}
          wip={wip}
          currentPage={currentPage}
          startPage={paging.startPage || 0}
          showNext={showNext || wip}
        />
        <Button
          className={classNames(styles.navBtn, styles['navBtn--next'])}
          iconButton={'kbase kbase-omni-chevron-right'}
          variant={'text'}
          color={'grey'}
          disabled={!showNext || wip}
          onClick={() => {
            const delta = paging.startPage === 0 ? 0 : 1
            changePage(currentPage + delta)
          }}
        />
      </div>
    </div>
  )
}

export default Paging
